import * as v from 'valibot'
import client from '@client-shared/utils/client-rest'
import { filterValuesSchema, geospatialDataGroupRecordSchema } from '../../schemas'
import ValibotCommon from '@shared-schemas/common'
import OriginDestination from '@shared-types/origin-destination'
import { getGeospatialDataGroupRecordSchema } from '@shared-schemas/origin-destination'
import { getCustomerIdFromCurrentRoute } from '@client-shared/utils/users'
import { handleAsync } from '@client-shared/utils/async'
import { od_event_emitter } from '../../od-event-emitter'
import type { UploadItem } from '../../util/uploads-manager'

export async function fetchGeospatialDataGroupRecords(params: {
	customer_id: string
	offset?: number
	limit?: number
	user_ids?: string[]
	date_range?: string
	date_range_start?: string
	date_range_end?: string
	name_query?: string
}) {
	const {
		customer_id,
		offset = 0,
		limit = 25,
		user_ids,
		date_range,
		date_range_start,
		date_range_end,
		name_query,
	} = params

	if (!customer_id.trim()) {
		throw new Error('No customer specified')
	}

	const query_params = new URLSearchParams()
	query_params.set('customer_id', customer_id)
	query_params.set('offset', offset.toString())
	query_params.set('limit', limit.toString())

	if (Array.isArray(user_ids) && user_ids.length) {
		query_params.set('users', user_ids.join(','))
	}

	if (date_range) {
		query_params.set('date_range', date_range)
	}

	if (date_range_start) {
		query_params.set('date_range_start', date_range_start)
	}

	if (date_range_end) {
		query_params.set('date_range_end', date_range_end)
	}

	if (name_query) {
		query_params.set('name_query', name_query)
	}

	const response = await client(`/api/v1/pages/origin-destination/geospatial-data-groups?${query_params.toString()}`)

	return v.parse(v.array(geospatialDataGroupRecordSchema), response)
}

export async function fetchFilterValues(params: { customer_id: string }) {
	const { customer_id } = params

	if (!customer_id.trim()) {
		throw new Error('No customer specified')
	}

	const query_params = new URLSearchParams()
	query_params.set('customer_id', customer_id)

	const response = await client(
		`/api/v1/pages/origin-destination/geospatial-data-groups/filter-values?${query_params.toString()}`,
	)

	return v.parse(filterValuesSchema, response)
}

export async function getUploadURL(params: { customer_id: string; filename: string }) {
	const { customer_id, filename } = params

	if (!customer_id.trim() || !filename.trim()) {
		console.error('getUploadURL must specify customer_id and filename')

		throw new Error('An error occurred while starting the upload')
	}

	const response = await client({
		url: '/api/v1/pages/origin-destination/file-library',
		method: 'POST',
		body: {
			customer_id: customer_id.trim(),
			filename: filename.trim(),
		},
	})

	return v.parse(
		v.object({
			uploaded_file_id: ValibotCommon.getUuidSchema({
				expect_short_uuid: true,
				error_message: 'uploaded_file_id is not a valid UUID',
			}),
			upload_url: v.pipe(v.string(), v.trim(), v.nonEmpty(), v.url()),
		}),
		response,
	)
}

export async function changeUploadStatus(
	uploaded_file_id: string,
	upload_status: OriginDestination.Enums.UploadedFileUploadStatus,
) {
	const response = await client({
		url: `/api/v1/pages/origin-destination/file-library/${uploaded_file_id}`,
		method: 'PUT',
		body: {
			upload_status,
		},
	})

	return v.parse(
		v.object({
			id: ValibotCommon.getUuidSchema({
				expect_short_uuid: true,
				error_message: 'id is not a valid UUID',
			}),
			upload_status: v.enum(OriginDestination.Enums.UploadedFileUploadStatus),
		}),
		response,
	)
}

export async function createGeospatialDataGroupRecord(params: { customer_id: string; uploaded_file_id: string }) {
	const { customer_id, uploaded_file_id } = params

	if (!customer_id.trim() || !uploaded_file_id.trim()) {
		console.error('createGeospatialDataGroupRecord must specify customer_id and uploaded_file_id')

		throw new Error('An error occurred while validating the uploaded file')
	}

	const response = await client({
		url: '/api/v1/pages/origin-destination/geospatial-data-groups',
		method: 'POST',
		body: {
			customer_id: customer_id.trim(),
			uploaded_file_id: uploaded_file_id.trim(),
		},
	})

	return v.parse(getGeospatialDataGroupRecordSchema({ expect_short_uuid: true }), response)
}

export async function updateGeospatialDataGroupRecord(
	record_id: string,
	customer_id: string,
	params: { display_name?: string } = {},
) {
	if (!record_id.trim() || !customer_id.trim()) {
		console.error('updateGeospatialDataGroupRecord must specifiy record_id and customer_id')

		throw new Error('An error occurred while updating the specified record')
	}

	const query_params = new URLSearchParams({ customer_id })

	const response = await client({
		url: `/api/v1/pages/origin-destination/geospatial-data-groups/${record_id}?${query_params.toString()}`,
		method: 'PUT',
		body: params,
	})

	return response
}

export async function deleteGeospatialDataGroupRecord(record_id: string, customer_id: string) {
	if (!record_id.trim() || !customer_id.trim()) {
		console.error('deleteGeospatialDataGroupRecord must specifiy record_id and customer_id')

		throw new Error('An error occurred while deleting the specified record')
	}

	const query_params = new URLSearchParams({ customer_id })

	const response = await client({
		url: `/api/v1/pages/origin-destination/geospatial-data-groups/${record_id}?${query_params.toString()}`,
		method: 'DELETE',
	})

	return response
}

export async function fetchSingleGeospatialDataGroupRecord(record_id: string, customer_id: string) {
	if (!record_id.trim() || !customer_id.trim()) {
		console.error('fetchSingleGeospatialDataGroupRecord must specifiy record_id and customer_id')

		throw new Error('An error occurred while fetching the specified record')
	}

	const query_params = new URLSearchParams({ customer_id })

	const response = await client({
		url: `/api/v1/pages/origin-destination/geospatial-data-groups/${record_id}?${query_params.toString()}`,
		method: 'GET',
	})

	return v.parse(geospatialDataGroupRecordSchema, response)
}

export async function createGroupRecordPostProcessor(item: UploadItem, emitUpdate: () => void) {
	// Call the create geospatial data group atlas-ui endpount.
	// The file will be validated and geospatial data group and entries will be created.

	item.post_processing_type = 'Validating Geospatial entities...'

	emitUpdate()

	const [created_group, create_group_error] = await handleAsync(
		createGeospatialDataGroupRecord({
			customer_id: getCustomerIdFromCurrentRoute(),
			uploaded_file_id: item.id,
		}),
	)

	if (create_group_error) {
		// TODO: send to Posthog
		console.error(create_group_error.message)

		throw new Error('An error occurred while validating the uploaded file. Please try again.')
	}

	od_event_emitter.emit('file-library:add', { record: created_group, origin_context: 'file-library' })
}

export async function getUploadedFileDownloadURL(uploaded_file_id: string, customer_id: string) {
	if (!uploaded_file_id.trim() || !customer_id.trim()) {
		console.error('getUploadedFileDownloadURL must specifiy uploaded_file_id and customer_id')

		throw new Error('An error occurred while fetching the specified record')
	}

	const query_params = new URLSearchParams({ customer_id })

	const response = await client({
		url: `/api/v1/pages/origin-destination/file-library/${uploaded_file_id}/download?${query_params.toString()}`,
		method: 'GET',
	})

	return v.parse(
		v.object({
			download_url: ValibotCommon.getNonEmptyStringSchema({ error_message: 'download_url is not a valid string' }),
		}),
		response,
	)
}
